import { Application } from '@enums/application.enums';

export const EVENT_TICKET_PREVIEW_ROUTE_SEGMENT = {
  TICKET_PURCHASE: 'ticket-purchase'
} as const;

export const EVENT_TICKET_PREVIEW_LABELS = {
  PAGE_TITLE: 'Ticket Purchase',
  BACK_ARIA: 'Go back',
  SELECT_TICKETS: 'Select Tickets',
  TICKET_LEVELS: 'Ticket Levels',
  NUMBER_OF_TICKETS: 'Number of Tickets',
  REQUIRED_SUFFIX: '*',
  MAX_TICKETS_HINT: 'Maximum {max} Tickets Per Person',
  REMAINING: 'only {count} tickets remaining',
  SOLD_OUT: 'Sold out',
  BUY_TICKETS: 'Buy tickets',
  IMPORTANT_INFO: 'Important Information',
  ALT_EVENT_IMAGE: 'Event preview',
  ALT_CREATOR_PROFILE: 'Creator profile',
  EMPTY_LEVELS: 'No ticket levels are available for this event.',
  PURCHASE_PREVIEW_INFO:
    'Ticket purchase is not available in preview. Please check back later.',
  NOT_NATIVE_TICKET: 'This event is not configured for native tickets.',
  NO_TICKETS_AVAILABLE: 'No tickets are available for the selected level.',
  SELECT_QUANTITY: 'Select at least one ticket.',
  CREATOR_NOT_RESOLVED:
    'Unable to load creator for checkout. Please refresh and try again.',
  USER_HAS_REACHED_LIMIT: 'You have exceeded the maximum ticket purchase limit for this level.',
  MINIMUM_AGE_REQUIRED: 'This is an age-restricted event. You are not allowed to purchase this ticket.',
  ATTENDEE_LIST: 'Attendee List',
  GOING: 'Going',
  VIEW_ALL: 'View All',
  ALT_ATTENDEE_AVATAR: 'Attendee profile',
  USER: 'User',
  ATTENDEES: 'Attendees',
  LOCK_NOTE_SUBSCRIBERS_ONLY: 'Only available to Subscribers',
  LOCK_NOTE_NFT_ONLY: 'Only available to NFT Holders',
  LOCK_NOTE_SUBSCRIBERS_AND_NFT: 'Only available to Subscribers or NFT Holders',
  ALREADY_HAVE_ACCESS: 'You already have access to this digital event.',
  UNLOCK_OPTIONS_HEADER: 'Unlock Ticket Access',
  UNLOCK_OPTIONS_DESCRIPTION:
    'This ticket level is only available to Subscribers or NFT Holders.',
  UNLOCK_OPTIONS_DESCRIPTION_DIGITAL:
    'This digital event is only available to Subscribers or NFT Holders.',
  SUBSCRIBE: 'SUBSCRIBE',
  NFT: 'NFT',
  OR: 'or',
  BUY_TICKET: 'Buy Ticket',
  DIGITAL_EVENT_TICKET: 'Digital Event Ticket',
  MY_TICKETS: 'My Tickets',
  SCROLL_TO_MY_TICKETS_ARIA: 'Scroll to My Tickets',
  PAST: 'Past'
} as const;

export const EVENT_TICKET_PREVIEW_NUM = {
  DEFAULT_QUANTITY: Application.ONE,
  MIN_QUANTITY: Application.ONE,
  ATTENDEE_AVATAR_SIZE: 48,
  ATTENDEE_AVATAR_OUTER_SIZE: 54,
  ATTENDEE_OVERFLOW_SIZE: 48,
  ATTENDEE_AVATAR_GAP: 12,
  /** Gap + overflow circle (48px) — one additional overflow slot */
  ATTENDEE_AVATAR_SLOT: 60
} as const;

export interface EventAttendeeRow {
  first_name: string;
  last_name: string;
  profile_url: string;
  gsi4pk: string;
  user_name: string;
  refer_id: string;
  ticket_count: number;
}

export interface EventAttendeesApiResponse {
  status: boolean;
  data: EventAttendeeRow[];
  count: number;
  total_ticket_count: number;
}

export function getEventAttendeeFullName(attendee: EventAttendeeRow): string {
  return `${attendee.first_name ?? ''} ${attendee.last_name ?? ''}`.trim();
}

function getAttendeeAvatarsWidth(avatarCount: number): number {
  if (avatarCount <= Application.ZERO) {
    return Application.ZERO;
  }
  const { ATTENDEE_AVATAR_OUTER_SIZE, ATTENDEE_AVATAR_GAP } =
    EVENT_TICKET_PREVIEW_NUM;
  return (
    ATTENDEE_AVATAR_OUTER_SIZE * avatarCount +
    ATTENDEE_AVATAR_GAP * (avatarCount - 1)
  );
}

function getAttendeeAvatarsWithOverflowWidth(avatarCount: number): number {
  const { ATTENDEE_AVATAR_OUTER_SIZE, ATTENDEE_OVERFLOW_SIZE, ATTENDEE_AVATAR_GAP } =
    EVENT_TICKET_PREVIEW_NUM;
  return (
    ATTENDEE_AVATAR_OUTER_SIZE * avatarCount +
    ATTENDEE_AVATAR_GAP * avatarCount +
    ATTENDEE_OVERFLOW_SIZE
  );
}

export function getVisibleEventAttendeesForWidth(
  attendees: EventAttendeeRow[],
  count: number,
  containerWidth: number
): { visible: EventAttendeeRow[]; overflowCount: number } {
  if (count <= Application.ZERO || containerWidth <= Application.ZERO) {
    return { visible: [], overflowCount: Application.ZERO as number };
  }

  const { ATTENDEE_AVATAR_SLOT } = EVENT_TICKET_PREVIEW_NUM;

  if (getAttendeeAvatarsWidth(count) <= containerWidth) {
    return { visible: attendees.slice(Application.ZERO, count), overflowCount: Application.ZERO as number };
  }

  let visibleAvatarCount = 0;
  for (let i = count - 1; i >= 0; i--) {
    if (getAttendeeAvatarsWithOverflowWidth(i) <= containerWidth) {
      visibleAvatarCount = i;
      break;
    }
  }

  const extraCount = count - visibleAvatarCount;
  if (extraCount <= Application.ZERO) {
    return { visible: attendees.slice(Application.ZERO, count), overflowCount: Application.ZERO as number };
  }

  const remainingWidth =
    containerWidth - getAttendeeAvatarsWidth(visibleAvatarCount);

  if (remainingWidth < ATTENDEE_AVATAR_SLOT) {
    return {
      visible: attendees.slice(Application.ZERO, visibleAvatarCount),
      overflowCount: extraCount
    };
  }

  if (remainingWidth === ATTENDEE_AVATAR_SLOT && extraCount === Application.ONE) {
    return {
      visible: attendees.slice(Application.ZERO, visibleAvatarCount + Application.ONE),
      overflowCount: Application.ZERO as number
    };
  }

  return {
    visible: attendees.slice(Application.ZERO, visibleAvatarCount),
    overflowCount: extraCount
  };
}
